/**
 * This session's tasks, as a chip in the session header (RFC 0319 3d).
 *
 * The session lens: the tasks whose refs name this session, grouped under
 * their parent, read from the project's task registry so nothing is opened to
 * draw it. It replaces the strip above the composer, which pushed the thread up
 * a row per task to say what the header can say in a word.
 *
 * THE SHARED LIST (RFC 0325). The rows, nesting, tick with undo and note, and
 * the › into a task's detail (history, where it lives, what closed it, who
 * worked it) all come from the SDK chip, the same as every task surface. This
 * wrapper only chooses the tasks and names the goal.
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import {
  type ProjectDoc,
  projectString,
  SessionTaskLog,
  sessionGoalTask,
  type TaskSummary,
} from '@mod/core/model'
import { TaskChip, type TaskElementRef, type TaskPort } from '@mod/sdk/ui'
import { useCallback, useMemo } from 'react'
import { sessionTaskGroups } from '../../services/tasks/registry'

/** A blocked task with nothing it waits on is waiting on a person. */
export function waitingOnYou(task: TaskSummary): boolean {
  return task.status === 'blocked' && task.blockedBy.length === 0
}

/** `github:owner/repo#12` as the page it names, or null for anything else. */
export function prUrl(target: string): string | null {
  const m = /^github:([\w.-]+)\/([\w.-]+)#(\d+)$/.exec(target)
  return m ? `https://github.com/${m[1]}/${m[2]}/pull/${m[3]}` : null
}

interface Props {
  repo: Repo
  projectId: string
  projectDoc: unknown
  sessionId: string
  onOpenDocument?: (documentId: string) => void
  /** Open the Tasks app. Absent when it is not installed. */
  onOpenApp?: () => void
  /** Name the session's current goal on the chip instead of "Tasks n/m". */
  showGoal?: boolean
}

export function SessionTasksChip({
  repo,
  projectId,
  projectDoc,
  sessionId,
  onOpenDocument,
  onOpenApp,
  showGoal = false,
}: Props) {
  const log = useMemo(
    () => new SessionTaskLog(repo as never, undefined, projectId),
    [repo, projectId],
  )
  const groups = useMemo(
    () => sessionTaskGroups(projectDoc, sessionId),
    [projectDoc, sessionId],
  )
  const rows = groups.flatMap((g) => g.tasks)
  // Each group's parent is listed too, so the shared list nests the session's
  // tasks under the work they belong to.
  const listed = useMemo(() => {
    const out: TaskSummary[] = []
    const seen = new Set<string>()
    for (const group of groups) {
      for (const task of [
        ...(group.parent ? [group.parent] : []),
        ...group.tasks,
      ]) {
        if (seen.has(task.id)) continue
        seen.add(task.id)
        out.push(task)
      }
    }
    return out
  }, [groups])
  const counted = rows.filter((task) => task.status !== 'cancelled')
  const done = counted.filter((task) => task.status === 'complete').length
  const goal = showGoal ? sessionGoalTask(rows, sessionId) : null
  const waiting = counted.some(waitingOnYou)
  const port = useMemo<TaskPort>(
    () => ({
      get: (id) => log.get(id),
      update: (id, patch) => log.update(id, patch),
      // The registry projection arrives through `projectDoc`, which rerenders
      // this wrapper and assigns the new task. The port still has the same
      // shape as a live app port; it simply needs no second subscription here.
      subscribe: () => () => {},
    }),
    [log],
  )
  const refLabels = useMemo(() => {
    const meta = (projectDoc as ProjectDoc | undefined)?.sessionMeta ?? {}
    const out: Record<string, string> = {}
    for (const [id, entry] of Object.entries(meta)) {
      const title = projectString((entry as { title?: unknown })?.title)
      if (title) out[id] = title
    }
    out[sessionId] = 'this one'
    return out
  }, [projectDoc, sessionId])
  const openRef = useCallback(
    (ref: TaskElementRef) => {
      if (ref.type === 'document') onOpenDocument?.(ref.target)
    },
    [onOpenDocument],
  )

  if (rows.length === 0) return null

  return (
    <TaskChip
      tasks={listed}
      port={port}
      label={
        goal
          ? `${goal.title} · ${done}/${counted.length}`
          : `Tasks ${done}/${counted.length}`
      }
      ariaLabel={`${done} of ${counted.length} session tasks complete`}
      popoverLabel="Tasks this session is working"
      onOpenApp={onOpenApp}
      includeCompleted
      popoverWidth={416}
      attention={waiting}
      refLabels={refLabels}
      onOpenRef={openRef}
    />
  )
}
